/* ============================================================
   Squeeze Cart - native cart drawer engine
   Replaces Rebuy Smart Cart. No dependencies.
   - Tiered free-gift auto add/remove (with stale-gift cleanup)
   - Shipping protection toggle (opt-in by default)
   - Multi-buy / automatic discount rendering from cart JSON
   - You May Also Like carousel via Section Rendering API
   - Discount codes (native cart API with legacy fallback)
   Graceful degradation: with JS off, cart links go to /cart.
   ============================================================ */
(function () {
  'use strict';

  var root = document.getElementById('SqueezeCart');
  var cfgEl = document.getElementById('SqueezeCartConfig');
  if (!root || !cfgEl) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!CFG || !CFG.enabled) return;
  /* shop.money_format can contain HTML (currency app spans) - we render plain text */
  CFG.moneyFormat = String(CFG.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');

  /* Keep pristine references BEFORE patching, for our own calls */
  var nativeFetch = window.fetch.bind(window);

  var ROUTES = {
    cartGet: '/cart.js',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
    checkout: '/checkout',
    recommendations: '/recommendations/products'
  };

  var TIERS = (CFG.tiers || []).filter(function (t) {
    return t && t.threshold > 0 && (t.kind === 'shipping' || t.variantId);
  }).sort(function (a, b) { return a.threshold - b.threshold; });
  var GIFT_VARIANTS = {};
  TIERS.forEach(function (t) { if (t.kind === 'gift' && t.variantId) GIFT_VARIANTS[t.variantId] = t; });
  var PROT = CFG.protection && CFG.protection.enabled && CFG.protection.variantId ? CFG.protection : null;
  /* gift mode, theme setting sq_gift_mode: 'stack' (default) gives every gift tier reached;
     'highest' gives only the highest one (BFCM 2026 USA: the $150 free 4in1 replaces the $99 Big Shot) */
  var GIFT_HIGHEST = CFG.giftMode === 'highest';

  var state = {
    cart: null,
    pm: null,                /* presentment money map (Shopify Markets), see refreshPMoney */
    open: false,
    syncing: false,
    dirty: false,
    reconcileFailures: {},   /* variantId -> true after a failed add, stops retry loops */
    recsKey: null,
    recsCount: 0,
    pendingCode: null,
    lastFocus: null
  };

  /* ---------------- utilities ---------------- */

  var toastTimer = null;
  function showToast(message) {
    var el = document.getElementById('SqueezeCartToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'SqueezeCartToast';
      el.setAttribute('role', 'alert');
      el.style.cssText = 'position:fixed;left:50%;bottom:28px;transform:translateX(-50%);background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:99px;font-size:14px;z-index:100000;box-shadow:0 4px 16px rgba(0,0,0,.25);max-width:90vw;text-align:center;';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.style.display = 'block';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.style.display = 'none'; }, 4000);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || CFG.moneyFormat || '${{amount}}';
    function withDelims(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) number = 0;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    var m = fmt.match(placeholderRegex);
    switch (m && m[1]) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  function resizeImage(url, size) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + size;
  }

  /* ---------------- presentment money (Shopify Markets) ----------------
     /cart.js returns money in the shop BASE currency (AUD). On a Markets store
     an international buyer (e.g. EUR) would then see AUD amounts in the drawer
     even though every other surface shows their local currency. Liquid `| money`
     IS Markets-aware, so we fetch a tiny section of presentment-formatted strings
     and use those for DISPLAY; /cart.js stays the source of truth for LOGIC.
     Everything falls back to base-currency formatMoney, so AU buyers and any
     fetch failure are unaffected. */
  var PRICES_SECTION = 'squeeze-cart-prices';

  function parseMoneyNum(s) {
    if (s == null) return 0;
    s = String(s).replace(/[^\d.,]/g, '');
    if (!s) return 0;
    var lastDot = s.lastIndexOf('.'), lastComma = s.lastIndexOf(',');
    if (lastComma > lastDot) { s = s.replace(/\./g, '').replace(',', '.'); }   /* 1.234,56 */
    else { s = s.replace(/,/g, ''); }                                          /* 1,234.56 */
    return parseFloat(s) || 0;
  }

  function refreshPMoney(done) {
    nativeFetch('/cart?section_id=' + PRICES_SECTION + '&_=' + (new Date().getTime()), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var pm = null;
        try {
          var el = new DOMParser().parseFromString(html, 'text/html').getElementById('sq-prices-data');
          if (el) pm = JSON.parse(el.textContent);
        } catch (e) { pm = null; }
        if (pm) {
          pm.symbol = String(pm.zero || '').replace(/[\d.,\s ]/g, '') || '$';
          var baseTotal = state.cart && state.cart.total_price;
          var presTotal = parseMoneyNum(pm.total);
          pm.rate = (baseTotal && presTotal) ? (presTotal * 100) / baseTotal : 1;
          state.pm = pm;
        }
        /* if parse failed, keep the last good pm (or null -> base fallback) */
      })
      .catch(function () { /* keep last good pm */ })
      .then(function () { if (done) done(); });
  }

  /* exact presentment string for a line price, base-currency fallback */
  function pmLine(it, which) {
    var k = it && it.key;
    if (state.pm && state.pm.items && state.pm.items[k] && state.pm.items[k][which] != null) {
      return state.pm.items[k][which];
    }
    return formatMoney(which === 'final' ? it.final_price : it.original_price);
  }
  /* exact presentment string for a cart total, base-currency fallback */
  function pmTotalStr(which, baseCents) {
    if (state.pm && state.pm[which] != null) return state.pm[which];
    return formatMoney(baseCents);
  }
  /* derived amounts (marker thresholds, "X away") converted via the cart's FX rate */
  function pmConvert(baseCents) {
    if (state.pm && state.pm.rate) {
      var v = (baseCents / 100) * state.pm.rate;
      var s = v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return (state.pm.symbol || '$') + s;
    }
    return formatMoney(baseCents);
  }

  function dismissedMap() {
    try { return JSON.parse(localStorage.getItem('sq_dismissed') || '{}'); } catch (e) { return {}; }
  }
  function isDismissed(variantId) {
    var map = dismissedMap();
    return !!map[variantId];
  }
  function setDismissed(variantId) {
    try {
      var map = dismissedMap();
      map[variantId] = Date.now();
      /* prune entries older than 60 days */
      Object.keys(map).forEach(function (k) {
        if (Date.now() - map[k] > 60 * 24 * 3600 * 1000) delete map[k];
      });
      localStorage.setItem('sq_dismissed', JSON.stringify(map));
    } catch (e) { /* private mode etc - dismissal then lasts for the pageview only */ }
  }
  function clearDismissed(variantId) {
    try {
      var map = dismissedMap();
      delete map[variantId];
      localStorage.setItem('sq_dismissed', JSON.stringify(map));
    } catch (e) {}
  }
  function clearAllDismissed() {
    try { localStorage.removeItem('sq_dismissed'); } catch (e) {}
  }

  function api(url, payload) {
    var opts = {
      method: payload ? 'POST' : 'GET',
      headers: { 'X-SQ-Internal': '1' },
      credentials: 'same-origin'
    };
    if (payload) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(payload);
    }
    return nativeFetch(url, opts).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) { var err = new Error('cart api ' + res.status); err.body = json; throw err; }
        return json;
      });
    });
  }

  /* ---------------- gift + protection reconciliation ---------------- */

  function isProtectionLine(item) {
    return PROT && item.variant_id === PROT.variantId;
  }
  function protectionLine(cart) {
    var found = null;
    (cart.items || []).forEach(function (it) { if (isProtectionLine(it)) found = it; });
    return found;
  }
  function visibleItems(cart) {
    return (cart.items || []).filter(function (it) { return !isProtectionLine(it); });
  }
  function tierSubtotal(cart) {
    /* discounted cart total minus shipping protection - gifts already contribute $0 */
    var total = cart.total_price || 0;
    var prot = protectionLine(cart);
    if (prot) total -= prot.final_line_price != null ? prot.final_line_price : prot.line_price;
    return total;
  }

  /* Decide mutations. Returns null when cart is already correct.
     Gift ops use line keys so propertied duplicates (e.g. lines an old Rebuy
     session added with _source properties) are handled deterministically. */
  function planOps(cart) {
    var subtotal = tierSubtotal(cart);
    var adds = [];
    var updates = {};
    var lineChanges = []; /* { key, quantity } applied one by one */
    var changed = false;

    var linesByVariant = {};
    (cart.items || []).forEach(function (it) {
      (linesByVariant[it.variant_id] = linesByVariant[it.variant_id] || []).push(it);
    });

    /* 1. tier gifts. In 'highest' gift mode only the highest gift tier reached qualifies,
       skipping one the customer removed or that could not be added, so the lower gift
       stays in that case */
    var topGift = null;
    if (GIFT_HIGHEST) {
      TIERS.forEach(function (t) {
        if (t.kind === 'gift' && t.variantId && subtotal >= t.threshold &&
            !isDismissed(t.variantId) && !state.reconcileFailures[t.variantId]) topGift = t;
      });
    }
    TIERS.forEach(function (t) {
      if (t.kind !== 'gift' || !t.variantId) return;
      var lines = linesByVariant[t.variantId] || [];
      var qualified = subtotal >= t.threshold && (!GIFT_HIGHEST || t === topGift);
      /* no dismissal check: gifts cannot be removed any more, and an old stored dismissal must not block one */
      if (qualified && !lines.length && !state.reconcileFailures[t.variantId]) {
        adds.push({ id: t.variantId, quantity: 1 });
        changed = true;
      } else if (!qualified && lines.length) {
        lines.forEach(function (l) { lineChanges.push({ key: l.key, quantity: 0 }); });
        changed = true;
      } else if (lines.length) {
        /* keep exactly one line at quantity 1, drop duplicates */
        lines.forEach(function (l, idx) {
          if (idx === 0 && l.quantity > 1) { lineChanges.push({ key: l.key, quantity: 1 }); changed = true; }
          if (idx > 0) { lineChanges.push({ key: l.key, quantity: 0 }); changed = true; }
        });
      }
    });

    /* 2. stale gifts: any $0-priced product that is not a currently configured tier gift
          (fixes old free items living in carts from past sales) */
    (cart.items || []).forEach(function (it) {
      if (isProtectionLine(it)) return;
      if (it.original_price === 0 && !GIFT_VARIANTS[it.variant_id]) {
        lineChanges.push({ key: it.key, quantity: 0 }); changed = true;
      }
    });

    /* 3. protection sanity: max quantity 1 */
    var prot = protectionLine(cart);
    if (prot && prot.quantity > 1) { updates[PROT.variantId] = 1; changed = true; }

    /* 4. protection pre-check (dormant unless enabled in theme settings) */
    if (PROT && PROT.prechecked && !prot && visibleItems(cart).length &&
        !isDismissed(PROT.variantId) && !state.reconcileFailures[PROT.variantId]) {
      adds.push({ id: PROT.variantId, quantity: 1 });
      changed = true;
    }

    return changed ? { adds: adds, updates: updates, lineChanges: lineChanges } : null;
  }

  function runOps(ops) {
    var chain = Promise.resolve();
    (ops.lineChanges || []).forEach(function (lc) {
      chain = chain.then(function () {
        return api(ROUTES.cartChange, { id: lc.key, quantity: lc.quantity }).catch(function () {});
      });
    });
    if (Object.keys(ops.updates).length) {
      chain = chain.then(function () { return api(ROUTES.cartUpdate, { updates: ops.updates }).catch(function () {}); });
    }
    if (ops.adds.length) {
      chain = chain.then(function () {
        return api(ROUTES.cartAdd, { items: ops.adds }).catch(function (err) {
          /* sold out / unavailable gift: remember and stop retrying */
          ops.adds.forEach(function (a) { state.reconcileFailures[a.id] = true; });
          console.warn('[SqueezeCart] gift add failed', err && err.body);
        });
      });
    }
    return chain;
  }

  /* ---------------- sync ---------------- */

  var syncTimer = null;
  var pendingOpen = false;
  function queueSync(openAfter) {
    /* each new cart change gets a fresh try at a gift add that failed before, instead of never for this page */
    state.reconcileFailures = {};
    pendingOpen = pendingOpen || !!openAfter;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var o = pendingOpen;
      pendingOpen = false;
      sync(o);
    }, 200);
  }

  function sync(openAfter) {
    if (state.syncing) {
      state.dirty = true;
      state.dirtyOpen = state.dirtyOpen || !!openAfter;
      return;
    }
    state.syncing = true;
    var attempts = 0;
    function step() {
      return api(ROUTES.cartGet).then(function (cart) {
        /* fresh start: an emptied cart wipes gift dismissals so rewards
           re-populate when thresholds are hit again (still max one of each) */
        if (!visibleItems(cart).length) clearAllDismissed();
        var ops = planOps(cart);
        if (ops && attempts < 3) {
          attempts += 1;
          return runOps(ops).then(step);
        }
        return cart;
      });
    }
    step().then(function (cart) {
      state.cart = cart;
      refreshPMoney(function () {
        render();
        if (openAfter) open();
      });
    }).catch(function (err) {
      console.warn('[SqueezeCart] sync failed', err);
    }).then(function () {
      state.syncing = false;
      if (state.dirty) {
        state.dirty = false;
        var reopen = state.dirtyOpen;
        state.dirtyOpen = false;
        queueSync(reopen);
      }
    });
  }

  /* ---------------- rendering ---------------- */

  var els = {
    count: root.querySelector('[data-sq-count]'),
    items: root.querySelector('[data-sq-items]'),
    empty: root.querySelector('[data-sq-empty]'),
    progress: root.querySelector('[data-sq-progress]'),
    progressMsg: root.querySelector('[data-sq-progress-message]'),
    progressFill: root.querySelector('[data-sq-progress-fill]'),
    markers: root.querySelector('[data-sq-markers]'),
    footer: root.querySelector('[data-sq-footer]'),
    protection: root.querySelector('[data-sq-protection]'),
    protectionToggle: root.querySelector('[data-sq-protection-toggle]'),
    total: root.querySelector('[data-sq-total]'),
    totalCompare: root.querySelector('[data-sq-total-compare]'),
    checkout: root.querySelector('[data-sq-checkout]'),
    recs: root.querySelector('[data-sq-recs]'),
    recsTrack: root.querySelector('[data-sq-recs-track]'),
    recsDots: root.querySelector('[data-sq-recs-dots]'),
    discountInput: root.querySelector('[data-sq-discount-input]'),
    discountApply: root.querySelector('[data-sq-discount-apply]'),
    discountMsg: root.querySelector('[data-sq-discount-msg]'),
    discountChips: root.querySelector('[data-sq-discount-chips]')
  };

  var TRUCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="5" width="13" height="11" rx="1"/><path d="M14 8h4l4 4v4h-3"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>';
  var CLOSE_SVG = '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l12 12M13 1L1 13"/></svg>';

  function buildMarkers() {
    if (!els.markers || !TIERS.length) return;
    var html = '';
    TIERS.forEach(function (t, i) {
      var pos = ((2 * i + 1) / (2 * TIERS.length)) * 100;
      var icon = t.kind === 'shipping'
        ? TRUCK_SVG
        : (t.image ? '<img src="' + esc(resizeImage(t.image, 100)) + '" alt="' + esc(t.label) + '">' : TRUCK_SVG);
      html += '<div class="sq-marker" data-threshold="' + t.threshold + '" style="left:' + pos.toFixed(2) + '%">' +
        '<div class="sq-marker__icon">' + icon + '</div>' +
        '<div class="sq-marker__label">' + esc(pmConvert(t.threshold).replace(/\.00\b/, '')) + '</div>' +
        '</div>';
    });
    els.markers.innerHTML = html;
  }

  function renderProgress(cart) {
    if (!els.progress) return;
    if (!TIERS.length || !visibleItems(cart).length) { els.progress.hidden = true; return; }
    els.progress.hidden = false;
    var subtotal = tierSubtotal(cart);

    /* piecewise fill so the bar lines up with evenly spaced markers */
    var n = TIERS.length;
    var pct = 0;
    var centers = TIERS.map(function (t, i) { return ((2 * i + 1) / (2 * n)) * 100; });
    if (subtotal <= 0) {
      pct = 0;
    } else if (subtotal >= TIERS[n - 1].threshold) {
      pct = 100;
    } else {
      var prevT = 0, prevC = 0;
      for (var i = 0; i < n; i++) {
        if (subtotal < TIERS[i].threshold) {
          var span = TIERS[i].threshold - prevT || 1;
          pct = prevC + ((subtotal - prevT) / span) * (centers[i] - prevC);
          break;
        }
        prevT = TIERS[i].threshold;
        prevC = centers[i];
      }
    }
    els.progressFill.style.width = Math.max(0, Math.min(100, pct)) + '%';

    Array.prototype.forEach.call(els.markers.children, function (el) {
      el.classList.toggle('sq-marker--reached', subtotal >= parseInt(el.getAttribute('data-threshold'), 10));
    });

    var next = null;
    for (var j = 0; j < n; j++) { if (subtotal < TIERS[j].threshold) { next = TIERS[j]; break; } }
    if (next) {
      els.progressMsg.innerHTML = 'You Are <strong>' + esc(pmConvert(next.threshold - subtotal)) +
        '</strong> Away From <strong>' + esc(next.label) + '</strong>';
    } else {
      els.progressMsg.innerHTML = '🎉 You’ve Unlocked All Rewards!';
    }
    /* keep the message on ONE line: shrink the font until it fits */
    els.progressMsg.style.fontSize = '';
    var fs = parseFloat(window.getComputedStyle(els.progressMsg).fontSize) || 13;
    var guard = 0;
    while (els.progressMsg.scrollWidth > els.progressMsg.clientWidth && fs > 10 && guard < 12) {
      fs -= 0.5;
      els.progressMsg.style.fontSize = fs + 'px';
      guard += 1;
    }
  }

  function lineDiscountTitle(item) {
    var alloc = item.line_level_discount_allocations;
    if (alloc && alloc.length && alloc[0].discount_application && alloc[0].discount_application.title) {
      return alloc[0].discount_application.title;
    }
    if (item.discounts && item.discounts.length && item.discounts[0].title) return item.discounts[0].title;
    return null;
  }

  function renderItems(cart) {
    var items = visibleItems(cart);
    if (!items.length) {
      els.items.innerHTML = '';
      els.empty.hidden = false;
      els.footer.hidden = true;
      return;
    }
    els.empty.hidden = true;
    els.footer.hidden = false;

    var html = items.map(function (it) {
      var isGift = it.original_price === 0;
      var img = it.image ? '<img src="' + esc(resizeImage(it.image, 200)) + '" alt="' + esc(it.product_title) + '" loading="lazy">' : '';
      var variantLine = (it.variant_title && it.variant_title !== 'Default Title')
        ? '<p class="sq-item__variant">' + esc(it.variant_title) + '</p>' : '';
      var badge = lineDiscountTitle(it);
      var badgeLine = badge ? '<p class="sq-item__badge">' + esc(badge) + '</p>' : '';
      var priceHtml;
      if (isGift) {
        priceHtml = '<span class="sq-item__free">' + esc(CFG.freeLabel || 'FREE') + '</span>';
      } else if (it.final_price != null && it.final_price < it.original_price) {
        priceHtml = '<span class="sq-price-final">' + esc(pmLine(it, 'final')) + '</span><s>' + esc(pmLine(it, 'original')) + '</s>';
      } else {
        priceHtml = '<span class="sq-price-final">' + esc(pmLine(it, 'original')) + '</span>';
      }
      var controls = isGift
        ? '<span class="sq-item__free">' + esc(CFG.freeLabel || 'FREE') + '</span>'
        : '<div class="sq-qty" data-key="' + esc(it.key) + '">' +
          '<button type="button" data-sq-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
          '<span>' + it.quantity + '</span>' +
          '<button type="button" data-sq-qty="1" aria-label="Increase quantity">+</button>' +
          '</div>';
      return '<div class="sq-item" data-key="' + esc(it.key) + '" data-variant="' + it.variant_id + '" data-gift="' + (isGift ? '1' : '0') + '">' +
        '<div class="sq-item__media"><a href="' + esc(it.url) + '">' + img + '</a></div>' +
        '<div class="sq-item__info">' +
          '<a class="sq-item__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
          variantLine + badgeLine +
          '<div class="sq-item__row">' + controls + '<div class="sq-item__price">' + (isGift ? '' : priceHtml) + '</div></div>' +
        '</div>' +
        /* free gifts have no remove button: a removed gift never came back while the bar said unlocked (#68080AU, 1 Nov 2026) */
        (isGift ? '' : '<button type="button" class="sq-item__remove" data-sq-remove aria-label="Remove ' + esc(it.product_title) + '">' + CLOSE_SVG + '</button>') +
        '</div>';
    }).join('');
    els.items.innerHTML = html;
  }

  function renderProtection(cart) {
    if (!els.protection) return;
    if (!PROT || !visibleItems(cart).length) { els.protection.hidden = true; return; }
    els.protection.hidden = false;
    els.protectionToggle.checked = !!protectionLine(cart);
  }

  function renderTotals(cart) {
    var total = cart.total_price || 0;
    var compare = cart.original_total_price || 0;
    els.total.textContent = pmTotalStr('total', total);
    if (compare > total) {
      els.totalCompare.hidden = false;
      els.totalCompare.textContent = pmTotalStr('originalTotal', compare);
    } else {
      els.totalCompare.hidden = true;
    }
  }

  function renderDiscounts(cart) {
    if (!els.discountChips) return;
    var html = '';
    var codes = [];
    if (cart.discount_codes && cart.discount_codes.length) {
      codes = cart.discount_codes.map(function (d) { return typeof d === 'string' ? { code: d, applicable: true } : d; });
    } else if (state.pendingCode) {
      codes = [{ code: state.pendingCode, applicable: true, pending: true }];
    }
    codes.forEach(function (d) {
      if (!d || !d.code) return;
      html += '<span class="sq-chip" data-code="' + esc(d.code) + '">' + esc(d.code) +
        (d.pending ? ' (at checkout)' : '') +
        '<button type="button" data-sq-remove-code="' + esc(d.code) + '" aria-label="Remove discount">' + CLOSE_SVG + '</button></span>';
    });
    els.discountChips.innerHTML = html;
  }

  function updateHeaderBubbles(count) {
    document.querySelectorAll('.cart-link__bubble').forEach(function (b) {
      b.classList.toggle('cart-link__bubble--visible', count > 0);
    });
  }

  function render() {
    var cart = state.cart;
    if (!cart) return;
    var prot = protectionLine(cart);
    var count = (cart.item_count || 0) - (prot ? prot.quantity : 0);
    els.count.textContent = count;
    updateHeaderBubbles(count);
    renderItems(cart);
    buildMarkers();            /* rebuild so threshold labels pick up presentment money */
    renderProgress(cart);
    renderProtection(cart);
    renderTotals(cart);
    renderDiscounts(cart);
    loadRecs(cart);
  }

  /* ---------------- recommendations ---------------- */

  function loadRecs(cart) {
    if (!CFG.recs || !CFG.recs.enabled || !els.recs) return;
    var firstPaid = null;
    visibleItems(cart).forEach(function (it) {
      if (!firstPaid && it.original_price > 0) firstPaid = it;
    });
    var key = firstPaid ? String(firstPaid.product_id) : 'generic';
    if (state.recsKey === key) { filterRecCards(cart); return; }
    state.recsKey = key;

    var url = firstPaid
      ? ROUTES.recommendations + '?section_id=' + CFG.recs.sectionId + '&product_id=' + firstPaid.product_id + '&limit=' + (CFG.recs.limit + 4) + '&intent=related'
      : '/?section_id=' + CFG.recs.sectionId;

    nativeFetch(url, { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (text) {
      var doc = document.createElement('div');
      doc.innerHTML = text;
      var cards = doc.querySelector('[data-sq-recs-cards]');
      if (!cards || !cards.children.length) {
        /* fallback: curated collection render via the home page context */
        if (firstPaid) {
          state.recsKey = 'generic';
          return nativeFetch('/?section_id=' + CFG.recs.sectionId, { credentials: 'same-origin' })
            .then(function (r2) { return r2.text(); })
            .then(function (t2) {
              var d2 = document.createElement('div');
              d2.innerHTML = t2;
              var c2 = d2.querySelector('[data-sq-recs-cards]');
              applyRecCards(c2);
            });
        }
        applyRecCards(null);
        return;
      }
      applyRecCards(cards);
    }).catch(function () { els.recs.hidden = true; });
  }

  function applyRecCards(cardsEl) {
    if (!cardsEl || !cardsEl.children.length) { els.recs.hidden = true; return; }
    els.recsTrack.innerHTML = cardsEl.innerHTML;
    filterRecCards(state.cart);
  }

  function filterRecCards(cart) {
    if (!els.recsTrack) return;
    var inCart = {};
    (cart.items || []).forEach(function (it) { inCart[it.product_id] = true; });
    var visible = 0;
    Array.prototype.forEach.call(els.recsTrack.querySelectorAll('[data-sq-card]'), function (card) {
      var pid = parseInt(card.getAttribute('data-product-id'), 10);
      var hide = !!inCart[pid] || (PROT && pid === PROT.productId);
      card.style.display = hide ? 'none' : '';
      if (!hide) visible += 1;
    });
    els.recs.hidden = visible === 0;
    if (visible > CFG.recs.limit) {
      var shown = 0;
      Array.prototype.forEach.call(els.recsTrack.querySelectorAll('[data-sq-card]'), function (card) {
        if (card.style.display === 'none') return;
        shown += 1;
        if (shown > CFG.recs.limit) card.style.display = 'none';
      });
      visible = CFG.recs.limit;
    }
    buildDots(visible);
  }

  function buildDots(count) {
    if (!els.recsDots) return;
    var html = '';
    for (var i = 0; i < count; i++) {
      html += '<button type="button" data-dot="' + i + '" aria-label="Go to recommendation ' + (i + 1) + '"' + (i === 0 ? ' class="is-active"' : '') + '></button>';
    }
    els.recsDots.innerHTML = html;
  }

  function visibleRecCards() {
    return Array.prototype.filter.call(els.recsTrack.querySelectorAll('[data-sq-card]'), function (c) {
      return c.style.display !== 'none';
    });
  }

  function updateDots() {
    var cards = visibleRecCards();
    if (!cards.length) return;
    var w = cards[0].offsetWidth + 10;
    var idx = Math.round(els.recsTrack.scrollLeft / w);
    Array.prototype.forEach.call(els.recsDots.children, function (d, i) {
      d.classList.toggle('is-active', i === Math.min(idx, els.recsDots.children.length - 1));
    });
  }

  /* ---------------- discount codes ---------------- */

  function discountMessage(text, ok) {
    if (!els.discountMsg) return;
    els.discountMsg.textContent = text || '';
    els.discountMsg.className = 'sq-cart__discount-msg' + (text ? (ok ? ' is-ok' : ' is-error') : '');
  }

  function applyDiscount(code) {
    code = (code || '').trim();
    if (!code) return;
    els.discountApply.disabled = true;
    discountMessage('');
    /* Modern cart API: discount codes applied straight to the cart */
    api(ROUTES.cartUpdate, { discount_code: code }).then(function (cart) {
      var applied = false, rejected = false;
      if (cart.discount_codes && cart.discount_codes.length) {
        cart.discount_codes.forEach(function (d) {
          var c = typeof d === 'string' ? d : d.code;
          var ok = typeof d === 'string' ? true : d.applicable !== false;
          if (c && c.toLowerCase() === code.toLowerCase()) { applied = ok; rejected = !ok; }
        });
      }
      if (applied) {
        state.pendingCode = null;
        state.cart = cart;
        refreshPMoney(render);
        discountMessage('Discount applied', true);
        els.discountInput.value = '';
      } else if (rejected) {
        /* code exists in cart but not applicable -> clear it again */
        api(ROUTES.cartUpdate, { discount_code: '' }).catch(function () {});
        discountMessage('That code isn’t valid for this cart', false);
      } else {
        legacyDiscount(code);
      }
    }).catch(function () {
      legacyDiscount(code);
    }).then(function () {
      els.discountApply.disabled = false;
    });
  }

  function legacyDiscount(code) {
    /* Old reliable: /discount/CODE sets the checkout cookie */
    nativeFetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).then(function () {
      state.pendingCode = code;
      renderDiscounts(state.cart || {});
      discountMessage('Code saved - it will apply at checkout', true);
      els.discountInput.value = '';
    }).catch(function () {
      discountMessage('Couldn’t apply that code right now', false);
    });
  }

  function removeDiscount(code) {
    state.pendingCode = null;
    api(ROUTES.cartUpdate, { discount_code: '' }).then(function (cart) {
      state.cart = cart;
      refreshPMoney(render);
    }).catch(function () { renderDiscounts(state.cart || {}); });
  }

  /* ---------------- open / close ---------------- */

  function open() {
    if (state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    root.dataset.state = 'open';
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('sq-cart-lock');
    var closeBtn = root.querySelector('.sq-cart__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    root.dataset.state = 'closed';
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('sq-cart-lock');
    if (state.lastFocus && state.lastFocus.focus) state.lastFocus.focus({ preventScroll: true });
  }

  /* ---------------- event wiring ---------------- */

  function bindEvents() {
    /* open drawer instead of navigating to /cart (capture phase beats theme handlers) */
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href], [data-sq-open]') : null;
      if (!a) return;
      if (a.hasAttribute && a.hasAttribute('data-sq-open')) {
        e.preventDefault(); e.stopPropagation(); open(); return;
      }
      var href = a.getAttribute('href') || '';
      var path;
      try { path = new URL(href, window.location.origin).pathname; } catch (err) { path = href.split('?')[0].split('#')[0]; }
      if (path === '/cart' || path === '/cart/' || a.classList.contains('js-drawer-open-cart')) {
        e.preventDefault();
        e.stopPropagation();
        sync(true);
        open();
      }
    }, true);

    /* shared ajax add used by both interception paths */
    function ajaxAddFromForm(form, btn) {
      if (btn) btn.disabled = true;
      nativeFetch(ROUTES.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-SQ-Internal': '1', 'Accept': 'application/json' },
        body: new FormData(form)
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw j; });
        return res.json();
      }).then(function () {
        sync(true);
      }).catch(function (err) {
        var msg = (err && (err.description || err.message)) || 'Could not add to cart';
        console.warn('[SqueezeCart] add to cart failed:', msg, err);
        showToast(msg);
      }).then(function () {
        if (btn) btn.disabled = false;
      });
    }

    function isAddForm(form) {
      return form && form.action && form.action.indexOf('/cart/add') > -1 && !form.hasAttribute('data-sq-native');
    }

    /* capture clicks on add-to-cart submit buttons BEFORE leftover app scripts
       (e.g. Globo preorder) can hijack them and swallow the submit */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('form[action*="/cart/add"] [type="submit"]') : null;
      if (!btn) return;
      var form = btn.form || btn.closest('form');
      if (!isAddForm(form)) return;
      e.preventDefault();
      e.stopPropagation();
      ajaxAddFromForm(form, btn);
    }, true);

    /* intercept add-to-cart form submits (quick shop, sticky bars, requestSubmit) */
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isAddForm(form)) return;
      e.preventDefault();
      e.stopPropagation();
      ajaxAddFromForm(form, form.querySelector('[type="submit"]'));
    }, true);

    /* drawer internal clicks */
    root.addEventListener('click', function (e) {
      var t = e.target;

      /* protection info popover (inside the label, so stop the checkbox toggling) */
      var help = t.closest('[data-sq-protection-help]');
      var pop = root.querySelector('[data-sq-protection-pop]');
      if (help && pop) {
        e.preventDefault();
        e.stopPropagation();
        var show = pop.hidden;
        pop.hidden = !show;
        help.setAttribute('aria-expanded', show ? 'true' : 'false');
        return;
      }
      if (pop && !pop.hidden && !t.closest('[data-sq-protection-pop]')) {
        pop.hidden = true;
        var hb = root.querySelector('[data-sq-protection-help]');
        if (hb) hb.setAttribute('aria-expanded', 'false');
      }

      if (t.closest('[data-sq-close]')) { e.preventDefault(); close(); return; }

      var qtyBtn = t.closest('[data-sq-qty]');
      if (qtyBtn) {
        var wrap = qtyBtn.closest('.sq-qty');
        var item = qtyBtn.closest('.sq-item');
        var key = wrap.getAttribute('data-key');
        var current = parseInt(wrap.querySelector('span').textContent, 10);
        var next = current + parseInt(qtyBtn.getAttribute('data-sq-qty'), 10);
        item.classList.add('sq-item--busy');
        api(ROUTES.cartChange, { id: key, quantity: Math.max(0, next) })
          .catch(function () {})
          .then(function () { queueSync(false); });
        return;
      }

      var removeBtn = t.closest('[data-sq-remove]');
      if (removeBtn) {
        var row = removeBtn.closest('.sq-item');
        var rowKey = row.getAttribute('data-key');
        var isGift = row.getAttribute('data-gift') === '1';
        if (isGift) setDismissed(parseInt(row.getAttribute('data-variant'), 10));
        row.classList.add('sq-item--busy');
        api(ROUTES.cartChange, { id: rowKey, quantity: 0 })
          .catch(function () {})
          .then(function () { queueSync(false); });
        return;
      }

      var addBtn = t.closest('[data-sq-add]');
      if (addBtn) {
        addBtn.disabled = true;
        api(ROUTES.cartAdd, { items: [{ id: parseInt(addBtn.getAttribute('data-sq-add'), 10), quantity: 1 }] })
          .catch(function () {})
          .then(function () { addBtn.disabled = false; queueSync(false); });
        return;
      }

      var codeBtn = t.closest('[data-sq-remove-code]');
      if (codeBtn) { removeDiscount(codeBtn.getAttribute('data-sq-remove-code')); return; }

      if (t.closest('[data-sq-checkout]')) {
        /* one last gift check before leaving: checkout takes the cart as it is (never holds the customer more than 2.5 s) */
        var gone = false;
        var go = function () { if (gone) return; gone = true; window.location.href = ROUTES.checkout; };
        var guard = setTimeout(go, 2500);
        state.reconcileFailures = {};
        api(ROUTES.cartGet).then(function (cart) {
          var ops = planOps(cart);
          return ops ? runOps(ops) : null;
        }).catch(function () {}).then(function () { clearTimeout(guard); go(); });
        return;
      }

      var arrow = t.closest('[data-sq-recs-arrow]');
      if (arrow) {
        var cards = visibleRecCards();
        if (cards.length) {
          var w = cards[0].offsetWidth + 10;
          els.recsTrack.scrollBy({ left: parseInt(arrow.getAttribute('data-sq-recs-arrow'), 10) * w, behavior: 'smooth' });
        }
        return;
      }

      var dot = t.closest('[data-dot]');
      if (dot && dot.parentElement === els.recsDots) {
        var cards2 = visibleRecCards();
        if (cards2.length) {
          var w2 = cards2[0].offsetWidth + 10;
          els.recsTrack.scrollTo({ left: parseInt(dot.getAttribute('data-dot'), 10) * w2, behavior: 'smooth' });
        }
      }
    });

    /* protection toggle */
    if (els.protectionToggle) {
      els.protectionToggle.addEventListener('change', function () {
        var want = els.protectionToggle.checked;
        els.protectionToggle.disabled = true;
        if (want) { clearDismissed(PROT.variantId); } else { setDismissed(PROT.variantId); }
        var p = want
          ? api(ROUTES.cartAdd, { items: [{ id: PROT.variantId, quantity: 1 }] })
          : api(ROUTES.cartUpdate, { updates: (function () { var u = {}; u[PROT.variantId] = 0; return u; })() });
        p.catch(function () {}).then(function () {
          els.protectionToggle.disabled = false;
          queueSync(false);
        });
      });
    }

    /* discount */
    if (els.discountApply) {
      els.discountApply.addEventListener('click', function () { applyDiscount(els.discountInput.value); });
      els.discountInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyDiscount(els.discountInput.value); }
      });
    }

    /* recs dots follow scroll */
    if (els.recsTrack) {
      var scrollTimer;
      els.recsTrack.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(updateDots, 80);
      }, { passive: true });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) close();
    });

    /* re-sync when restored from bfcache */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) queueSync(false);
    });
  }

  /* ---------------- observe other scripts' cart calls ---------------- */

  function isCartMutation(url) {
    return /\/cart\/(add|update|change|clear)/.test(url || '');
  }

  function patchNetwork() {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var p = origFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var internal = init && init.headers && (init.headers['X-SQ-Internal'] || (init.headers.get && init.headers.get('X-SQ-Internal')));
        if (isCartMutation(url) && !internal) {
          p.then(function (res) {
            if (res && res.ok) queueSync(url.indexOf('/cart/add') > -1);
          }).catch(function () {});
        }
      } catch (e) { /* never break the page over observation */ }
      return p;
    };

    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__sqUrl = url;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (isCartMutation(this.__sqUrl)) {
        var xhr = this;
        this.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) queueSync((xhr.__sqUrl || '').indexOf('/cart/add') > -1);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  /* ---------------- boot ---------------- */

  buildMarkers();
  bindEvents();
  patchNetwork();
  /* landing directly on /cart (e.g. checkout's "Cart" breadcrumb) opens the
     drawer over the page; the page itself stays as the no-JS fallback */
  sync(/^\/cart\/?$/.test(window.location.pathname));
})();
